<template>
  <div>
    <identity-verification-form
      :form-model="formModel"
      :person-title="local.bankruptcy.common.theCreditor"
      :view-mode="identityFormDisabled || viewMode || loading"
      :is-edit-mode="isEditMode"
      :manager-social-id-required="false"
      :request-owner-type-id="requestOwnerTypeId"
      :main-request-details-types="mainRequestDetailsTypesEnum.bankruptDebts"
      :is-gov-sub-request="isGovSubRequest"
      @isIdentityVerified="$emit('isIdentityVerified', $event)"
    ></identity-verification-form>

    <naj-dynamic-form
      :form-model="formModel"
      :form-fields="formFields"
      :view-mode="loading || viewMode"
      @externalEffectEvent="externalEffectEventHandler"
    ></naj-dynamic-form>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapActions } = createNamespacedHelpers('BankruptcyModule');
import IdentityVerificationForm from '../../../identity-verification-form.vue';
import { paymentStatusEnum, creditorClassificationsEnum, mainRequestDetailsTypesEnum, partyTypeEnum } from '@/modules/bankruptcy/helpers/enum';

export default {
  inject: ['$validator'],
  components: { IdentityVerificationForm },
  props: {
    formModel: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    identityFormDisabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    applyMinDebtAmount: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    minDebtAmount: {
      type: Number,
      required: false,
      default: () => null,
    },
    viewMode: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    requestOwnerTypeId: {
      type: Number,
      required: false,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isGovSubRequest: {
      type: Boolean,
      required: false,
      default: () => false,
    }
  },
  data() {
    return {
      locale: this.local.bankruptcy.requestDetails,
      paymentStatusEnum,
      creditorClassificationsEnum,
      mainRequestDetailsTypesEnum,
      partyTypeEnum
    };
  },
  computed: {
    ...mapState(['countries', 'countryCities', 'yesNoOptions', 'paymentStatuses', 'creditorClassifications', 'currentUser']),
    isGovernmentOrForeignCompany(){
      const validTypes = [partyTypeEnum.government, partyTypeEnum.foreignCompany];
      return validTypes.indexOf(this.formModel.typeId) === -1;
    },
    validatingEmail(){
      if(this.formModel.typeId != partyTypeEnum.government)
      {
        return {
                email: true,
                no_gov_email: true,
              }
      }
      else 
      { return {
                email: true,
              }
      }
    },
    formFields() {
      return [
        {
          typeId: this.formFieldType.text,
          name: 'mobile',
          label: this.locale.bankruptDebts.mobile,
          columnSize: 6,
          properties: JSON.stringify({
            validations: {
              numeric: true,
              phone: true,
              digits: 10,
            },
            if: `${this.isGovernmentOrForeignCompany}`,
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.text,
          name: 'email',
          label: this.locale.bankruptDebts.email,
          columnSize: 6,
          properties: JSON.stringify({
            validations: this.validatingEmail,
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.text,
          name: 'debtValue',
          label: this.locale.bankruptDebts.debtValue,
          columnSize: 6,
          properties: JSON.stringify({
            validations: {
              required: true,
              decimal: 2,
              min_value: this.applyMinDebtAmount && this.minDebtAmount ? this.minDebtAmount : '0',
              isValid: `${this.locale.bankruptDebts.invalidDebtAmountValue}, ${this.applyMinDebtAmount && this.minDebtAmount} ? obj.debtValue>= ${this.minDebtAmount} :true`,
              max_number_length: 20,
            },
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.date,
          name: 'dueDate',
          label: this.locale.bankruptDebts.dueDate,
          columnSize: 6,
          properties: JSON.stringify({
            validations: { required: true },
            dateConfig: { acceptFutureDate: true },
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.text,
          name: 'debtOrigin',
          label: this.locale.bankruptDebts.debtOrigin,
          columnSize: 6,
          properties: JSON.stringify({
            validations: { required: true },
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.selectbox,
          name: 'countryId',
          label: this.locale.bankruptDebts.countryId,
          columnSize: 3,
          properties: JSON.stringify({
            validations: { required: true },
            dataSource: this.countries,
            hasExternalEffect: true,
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.selectbox,
          name: 'cityId',
          label: this.locale.bankruptDebts.cityId,
          columnSize: 3,
          properties: JSON.stringify({
            validations: { required: true },
            textName: 'cityName',
            if: (!!(this.cities || []).length).toString(),
            dataSource: this.cities,
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.text,
          name: 'cityName',
          label: this.locale.bankruptDebts.cityId,
          columnSize: 3,
          properties: JSON.stringify({
            validations: { required: true },
            if: (!(this.cities || []).length).toString(),
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.selectbox,
          name: 'paymentStatusId',
          label: this.locale.bankruptDebts.paymentStatusId,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              required: true,
              isValid: `${this.locale.bankruptDebts.invalidDebtAmount}, obj.paymentStatusId == ${paymentStatusEnum.partiallyPaid}? obj.receivedPayment*1 + obj.remainingPayment*1 == obj.debtValue :true`,
            },
            dataSource: this.paymentStatuses,
            hasExternalEffect: true,
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.text,
          name: 'receivedPayment',
          label: this.locale.bankruptDebts.receivedPayment,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              conditionalRequired: `obj.paymentStatusId == ${paymentStatusEnum.partiallyPaid}`,
              decimal: 2,
              min_value: '0',
              max_number_length: 20,
            },
            disabledIf: `obj.paymentStatusId != ${paymentStatusEnum.partiallyPaid}`,
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.text,
          name: 'remainingPayment',
          label: this.locale.bankruptDebts.remainingPayment,
          columnSize: 4,
          properties: JSON.stringify({
            validations: {
              conditionalRequired: `obj.paymentStatusId == ${paymentStatusEnum.partiallyPaid}`,
              decimal: 2,
              min_value: '0',
              max_number_length: 20,
            },
            disabledIf: `obj.paymentStatusId != ${paymentStatusEnum.partiallyPaid}`,
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.selectbox,
          name: 'creditorClassificationId',
          label: this.locale.bankruptDebts.creditorClassificationId,
          columnSize: 3,
          properties: JSON.stringify({
            validations: { required: true },
            dataSource: this.creditorClassifications,
            hasExternalEffect: true,
            isDimmed: this.currentUser.loginType == 'governmental' || this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.text,
          name: 'otherCreditorClassification',
          label: this.locale.bankruptDebts.otherCreditorClassification,
          columnSize: 3,
          properties: JSON.stringify({
            validations: { conditionalRequired: `obj.creditorClassificationId == ${creditorClassificationsEnum.other}` },
            disabledIf: `obj.creditorClassificationId != ${creditorClassificationsEnum.other}`,
            isDimmed: this.viewMode,
          }),
        },
        {
          typeId: this.formFieldType.radioButton,
          name: 'isGuaranteed',
          label: this.locale.bankruptDebts.isGuaranteed,
          columnSize: 3,
          properties: JSON.stringify({
            dataSource: this.yesNoOptions,
            initialValue: false,
            hasExternalEffect: true,
          }),
        },
        {
          typeId: this.formFieldType.text,
          name: 'guaranteeType',
          label: this.locale.bankruptDebts.guaranteeType,
          columnSize: 3,
          properties: JSON.stringify({
            validations: { conditionalRequired: 'obj.isGuaranteed == true' },
            disabledIf: 'obj.isGuaranteed != true',
            isDimmed: this.viewMode,
          }),
        },
      ];
    },
    cities() {
      return this.countryCities[this.formModel.countryId];
    },
  },
  mounted() {
    this.getCountries();
  },
  watch: {
    'formModel.countryId'(newValue) {
      this.getCountryCities(newValue);
    },
  },
  methods: {
    ...mapActions(['getCountries', 'getCountryCities']),
    externalEffectEventHandler({ name, value }) {
      if (name === 'isGuaranteed' && value === false) {
        this.$set(this.formModel, 'guaranteeType', null);
      }

      if (name === 'creditorClassificationId' && value !== this.creditorClassificationsEnum.other) {
        this.$set(this.formModel, 'otherCreditorClassification', null);
      }

      if (name === 'paymentStatusId' && value !== this.paymentStatusEnum.partiallyPaid) {
        this.$set(this.formModel, 'receivedPayment', null);
        this.$set(this.formModel, 'remainingPayment', null);
      }

      if (name === 'countryId') {
        if (value)
          this.getCountryCities(value);
        this.$set(this.formModel, 'cityName', null);
        this.$set(this.formModel, 'cityId', null);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
